import consumer from "./consumer"
import { showError } from "../lib/notifications"
import { confirmWithModal } from "../lib/modal_confirm"
import { getCsrfToken } from "../lib/csrf_token"

const WorkerChannel = consumer.subscriptions.create("WorkerChannel", {
  _everConnected: false,
  _isConnected: false,
  _lastDisconnectedNoticeAt: null,
  _disconnectFlashEl: null,

  _isLoggedIn() {
    return !!document.body.dataset.currentWorkerId;
  },

  connected() {
    if (this._disconnectFlashEl) {
      this._disconnectFlashEl.remove();
      this._disconnectFlashEl = null;
    }
    if (this._everConnected && this._isLoggedIn()) {
      this._showFlash("Reconnected — you can now select blocks.", "notice");
    }
    this._everConnected = true;
    this._isConnected = true;
  },

  disconnected() {
    this._isConnected = false;
    // Don't show any message on disconnect - only show when user tries to act
  },

  received(data) {
    if (data.event === "dig_ack") {
      // console.log(data)
    }

    if (data.type === "banned") {
      showError(data.message, { duration: 8000 });
      setTimeout(() => {
        window.location.href = "/banned";
      }, 400);
      return;
    }

    if (data.type === "async_block_with_unlock" && data.show_modal) {
      this.handleAsyncBlockWithUnlock(data.message);
      return;
    }

    if (data.type === "flash_message") {
      this._showFlash(data.message, data.message_type);
      return;
    }
  },

  async handleAsyncBlockWithUnlock(message) {
    const confirmed = await confirmWithModal(message || "You must exit Async Division to perform this action.");
    if (confirmed) {
      const csrfToken = getCsrfToken();
      if (!csrfToken) {
        this._showFlash("Security error: missing CSRF token. Please refresh the page and try again.", "alert");
        return;
      }

      try {
        const response = await fetch("/idle/leave", {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken
          }
        });
        if (response.ok) {
          this._showFlash("Left Async Division", "notice");
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } else {
          console.error("Failed to leave async division: HTTP", response.status, response.statusText);
          this._showFlash("Failed to leave Async Division", "alert");
        }
      } catch (error) {
        console.error("Failed to leave async division:", error);
        this._showFlash("Failed to leave Async Division", "alert");
      }
    }
  },

  performAction(action, payload = {}) {
    if (!this._isConnected) {
      this.showDisconnectedMessageThrottled();
      return;
    }
    this.perform(action, payload);
  },

  showDisconnectedMessageThrottled() {
    if (!this._isLoggedIn()) return;
    const now = Date.now();
    const THROTTLE_MS = 5000;

    if (this._lastDisconnectedNoticeAt && (now - this._lastDisconnectedNoticeAt) < THROTTLE_MS) {
      return;
    }

    this._lastDisconnectedNoticeAt = now;
    this._showFlash("Connection lost — please wait for reconnection before selecting blocks.", "alert", true);
  },

  _showFlash(message, type = "notice", persistent = false) {
    const container = document.getElementById('flash-messages')
    if (!container) return

    // Replace existing persistent flash with new one
    if (persistent && this._disconnectFlashEl) {
      this._disconnectFlashEl.remove()
      this._disconnectFlashEl = null
    }

    const borderVariant = type === 'alert' || type === 'error' ? 'border-error' : 'border-success'
    const flash = document.createElement('div')
    flash.className = `card relative shadow-md bg-base-100 border border-base-300 border-l-4 ${borderVariant}`
    flash.setAttribute('role', 'alert')

    // Auto-dismiss via flash controller, persistent via inline click
    if (!persistent) {
      flash.setAttribute('data-controller', 'flash')
    }

    const dismissAction = persistent ? `onclick="this.closest('.card').remove()"` : `data-action="click->flash#dismissAll"`
    flash.innerHTML = `
      <div class="card-body p-3">
        <button type="button"
                class="btn btn-ghost btn-xs absolute top-0 right-0 w-10 h-10 min-h-0 p-0 leading-none"
                aria-label="Dismiss notification"
                ${dismissAction}>
          <span class="text-xs">x</span>
        </button>
        <div class="text-xs leading-snug break-words pr-5"></div>
      </div>
    `

    const messageDiv = flash.querySelector('.text-xs.leading-snug.break-words.pr-5')
    if (messageDiv) {
      messageDiv.textContent = message
    }

    container.insertAdjacentElement('afterbegin', flash)

    // Track persistent flash for cleanup
    if (persistent) {
      this._disconnectFlashEl = flash
    }
  },
});

window.WorkerChannel = WorkerChannel;
